import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Hash, ChevronLeft, Sparkles, Calculator, User, Calendar, ArrowRight, Loader2, Star, Brain, Heart, Eye, Lock, Save, History } from 'lucide-react';
import { useAuth } from '@/components/AuthContext';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NumberCard = ({ title, number, subtitle, icon: Icon, color, meaning, isLocked = false }) => {
  const [expanded, setExpanded] = useState(false);
  
  const colorClasses = {
    emerald: 'from-emerald-500 to-teal-600 border-emerald-200 bg-emerald-50',
    purple: 'from-purple-500 to-indigo-600 border-purple-200 bg-purple-50',
    amber: 'from-amber-500 to-orange-600 border-amber-200 bg-amber-50',
    blue: 'from-blue-500 to-cyan-600 border-blue-200 bg-blue-50',
  };
  
  if (isLocked) {
    return (
      <div className="relative p-6 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 text-center">
        <Lock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-xs text-gray-400 mt-1">Premium</p>
      </div>
    );
  }
  
  return (
    <motion.div
      layout
      className={`relative p-6 rounded-2xl border-2 ${colorClasses[color].split(' ').slice(1).join(' ')} cursor-pointer transition-all duration-300 hover:shadow-lg`}
      onClick={() => setExpanded(!expanded)}
    >
      {/* Icon */}
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[color].split(' ').slice(0, 2).join(' ')} flex items-center justify-center mb-4`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      
      {/* Number */}
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-4xl font-bold text-gray-900">{number}</span>
        {meaning?.is_master && (
          <span className="px-2 py-1 text-xs font-semibold bg-amber-100 text-amber-700 rounded-full">
            Maître
          </span>
        )}
      </div>
      
      {/* Title */}
      <h3 className="font-heading text-lg font-semibold text-gray-800 mb-1">{title}</h3>
      {subtitle && <p className="text-sm text-gray-500 mb-3">{subtitle}</p>}
      
      {/* Meaning Preview */}
      {meaning && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">{meaning.name}</p>
          <div className="flex flex-wrap gap-1">
            {meaning.keywords?.slice(0, 3).map((kw, i) => (
              <span key={i} className="px-2 py-1 text-xs bg-white/50 rounded-full text-gray-600">
                {kw}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Expanded Content */}
      <AnimatePresence>
        {expanded && meaning && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 pt-4 border-t border-gray-200"
          >
            <div className="space-y-3">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Forces</p>
                <div className="flex flex-wrap gap-1">
                  {meaning.strengths?.map((s, i) => (
                    <span key={i} className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded-full">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Défis</p>
                <div className="flex flex-wrap gap-1">
                  {meaning.challenges?.map((c, i) => (
                    <span key={i} className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded-full">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
              {meaning.element && (
                <p className="text-xs text-gray-500">
                  Élément: <span className="font-medium">{meaning.element}</span> • 
                  Planète: <span className="font-medium">{meaning.planet}</span>
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <p className="text-xs text-gray-400 mt-3">
        {expanded ? 'Cliquez pour réduire' : 'Cliquez pour détails'}
      </p>
    </motion.div>
  );
};

const NumerologiaThemePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    birthName: ''
  });
  
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(false);
  const [interpretation, setInterpretation] = useState(null);
  const [loadingInterpretation, setLoadingInterpretation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [profilePreFilled, setProfilePreFilled] = useState(false);

  // Auto-fill from user profile
  useEffect(() => {
    if (isAuthenticated && user && !profilePreFilled) {
      const newFormData = { ...formData };
      let hasPreFilled = false;
      
      if (user.first_name || user.name) {
        newFormData.firstName = user.first_name || user.name;
        hasPreFilled = true;
      }
      if (user.birth_date) {
        newFormData.birthDate = user.birth_date;
        hasPreFilled = true;
      }
      
      if (hasPreFilled) {
        setFormData(newFormData);
        setProfilePreFilled(true);
        toast.success('Données pré-remplies depuis votre profil !', {
          description: 'Ajoutez votre nom de famille pour calculer'
        });
      }
    }
  }, [isAuthenticated, user, profilePreFilled]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.birthDate) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    
    try {
      // Use basic endpoint for free version, full for premium
      const endpoint = isAuthenticated ? '/api/numerologia/theme' : '/api/numerologia/theme/basic';
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          birth_date: formData.birthDate,
          birth_name: formData.birthName || null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTheme(data.theme);
        toast.success('Thème calculé avec succès !');
      } else {
        toast.error('Erreur lors du calcul');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const getAIInterpretation = async () => {
    if (!theme) return;
    
    setLoadingInterpretation(true);
    
    try {
      const response = await fetch(`${API_URL}/api/numerologia/interpret`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          theme_data: theme,
          focus: 'general'
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setInterpretation(data.interpretation);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de l\'interprétation');
    } finally {
      setLoadingInterpretation(false);
    }
  };

  const saveToHistory = async () => {
    if (!theme || !isAuthenticated || !user) {
      toast.error('Connectez-vous pour sauvegarder');
      return;
    }
    
    setSaving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/numerologia/history/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id || user._id,
          theme_data: theme,
          label: null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSaved(true);
        toast.success('Thème sauvegardé dans l\'historique !');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-emerald-100">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate('/numerologia')}
            className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors"
            data-testid="back-to-numerologia"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <Hash className="h-5 w-5 text-emerald-600" />
            <span className="font-heading text-lg font-bold text-emerald-700">
              Thème Fondamental
            </span>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Form Section */}
          {!theme && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-md mx-auto"
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Calculator className="h-8 w-8 text-white" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                  Calculez votre thème
                </h1>
                <p className="text-gray-600">
                  Entrez vos informations pour découvrir vos nombres clés
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">Prénom *</Label>
                    <Input
                      id="firstName"
                      data-testid="input-firstname"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      placeholder="Marie"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Nom *</Label>
                    <Input
                      id="lastName"
                      data-testid="input-lastname"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      placeholder="Dupont"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="birthDate">Date de naissance *</Label>
                  <Input
                    id="birthDate"
                    data-testid="input-birthdate"
                    type="date"
                    value={formData.birthDate}
                    onChange={(e) => setFormData({...formData, birthDate: e.target.value})}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="birthName">Nom de naissance (optionnel)</Label>
                  <Input
                    id="birthName"
                    data-testid="input-birthname"
                    value={formData.birthName}
                    onChange={(e) => setFormData({...formData, birthName: e.target.value})}
                    placeholder="Si différent du nom actuel"
                    className="mt-1"
                  />
                </div>
                
                <Button
                  type="submit"
                  data-testid="calculate-theme-btn"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-6"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Calcul en cours...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-5 w-5" />
                      Calculer mon thème
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          )}

          {/* Results Section */}
          {theme && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-8"
            >
              {/* Header */}
              <div className="text-center">
                <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                  Thème de {theme.identity?.first_name} {theme.identity?.last_name}
                </h1>
                <p className="text-gray-600">
                  Né(e) le {new Date(theme.identity?.birth_date).toLocaleDateString('fr-FR')}
                </p>
              </div>

              {/* Core Numbers */}
              <div>
                <h2 className="font-heading text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Star className="h-5 w-5 text-emerald-600" />
                  Vos nombres fondamentaux
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <NumberCard
                    title="Chemin de vie"
                    subtitle="Votre mission de vie"
                    number={theme.core_numbers?.life_path?.number}
                    meaning={theme.core_numbers?.life_path?.meaning}
                    icon={Brain}
                    color="emerald"
                  />
                  
                  <NumberCard
                    title="Expression"
                    subtitle="Vos talents naturels"
                    number={theme.core_numbers?.expression?.number}
                    meaning={theme.core_numbers?.expression?.meaning}
                    icon={User}
                    color="purple"
                  />
                  
                  <NumberCard
                    title="Nombre intime"
                    subtitle="Vos désirs profonds"
                    number={theme.core_numbers?.soul?.number}
                    meaning={theme.core_numbers?.soul?.meaning}
                    icon={Heart}
                    color="amber"
                  />
                  
                  {theme.core_numbers?.personality ? (
                    <NumberCard
                      title="Réalisation"
                      subtitle="Comment les autres vous voient"
                      number={theme.core_numbers?.personality?.number}
                      meaning={theme.core_numbers?.personality?.meaning}
                      icon={Eye}
                      color="blue"
                    />
                  ) : (
                    <NumberCard
                      title="Réalisation"
                      icon={Eye}
                      color="blue"
                      isLocked={true}
                    />
                  )}
                </div>
              </div>

              {/* Karmic Info (if available) */}
              {theme.core_numbers?.life_path?.karmic_debt && (
                <div className="p-6 bg-amber-50 rounded-2xl border border-amber-200">
                  <h3 className="font-heading text-lg font-semibold text-amber-800 mb-2">
                    Dette karmique détectée
                  </h3>
                  <p className="text-amber-700">{theme.core_numbers.life_path.karmic_debt.lesson}</p>
                  <p className="text-sm text-amber-600 mt-2">{theme.core_numbers.life_path.karmic_debt.description}</p>
                </div>
              )}

              {/* Free version upgrade prompt */}
              {theme.is_free_version && (
                <div className="p-6 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-2xl border border-emerald-200 text-center">
                  <Lock className="h-8 w-8 text-emerald-600 mx-auto mb-3" />
                  <h3 className="font-heading text-lg font-semibold text-emerald-800 mb-2">
                    Version gratuite
                  </h3>
                  <p className="text-emerald-700 mb-4">{theme.upgrade_message}</p>
                  <Button 
                    onClick={() => navigate('/auth')}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                  >
                    Créer un compte gratuit
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* AI Interpretation */}
              <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
                <h3 className="font-heading text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-emerald-600" />
                  Interprétation personnalisée
                </h3>
                
                {interpretation ? (
                  <div className="prose prose-sm max-w-none text-gray-700">
                    <ReactMarkdown>{interpretation}</ReactMarkdown>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Button
                      onClick={getAIInterpretation}
                      disabled={loadingInterpretation}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                      data-testid="get-interpretation-btn"
                    >
                      {loadingInterpretation ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Analyse en cours...
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 h-5 w-5" />
                          Obtenir l'interprétation IA
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => { setTheme(null); setSaved(false); }}
                  className="rounded-xl"
                >
                  Nouveau calcul
                </Button>
                
                {isAuthenticated && !saved && (
                  <Button
                    variant="outline"
                    onClick={saveToHistory}
                    disabled={saving}
                    className="rounded-xl border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                    data-testid="save-theme-btn"
                  >
                    {saving ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    Sauvegarder
                  </Button>
                )}
                
                {saved && (
                  <Button
                    variant="outline"
                    onClick={() => navigate('/numerologia/history')}
                    className="rounded-xl border-emerald-500 text-emerald-700 bg-emerald-50"
                  >
                    <History className="mr-2 h-4 w-4" />
                    Voir l'historique
                  </Button>
                )}
                
                <Button
                  onClick={() => navigate('/numerologia/cycles')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                >
                  Voir mes cycles
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NumerologiaThemePage;
